/** @jsxImportSource @emotion/react */

import styled from "@emotion/styled";
import { Fragment } from "react";

import React from "react";

import PhotoSizeSelectIcon from "@material-ui/icons/PhotoSizeSelectLarge";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import TextIcon from "@material-ui/icons/Title";
import LayersIcon from "@material-ui/icons/Layers";
import ViewCompact from "@material-ui/icons/ViewCompact";
import PrintplateIcon from "@material-ui/icons/ViewQuilt";
import EditorIcon from "@material-ui/icons/PermMedia";
import ColorIcon from "@material-ui/icons/ColorLens";
import OrdersIcon from "@material-ui/icons/ViewList";
import SettingsIcon from "@material-ui/icons/Settings";

import MenuHideButton from "./tools/MenuHideButton";

import ToolbarButton from "./ToolbarButton";
import SizeManager from "app/size/SizeManager";

import Gallery from "./tools/Gallery";
import Uploader from "./tools/Uploader";
import BackgroundColorPicker from "./tools/BackgroundColorPicker";
import LayersToolbar from "./tools/Layers";

import EditorsList from "./admin/EditorsList";
import PrintplatesList from "./admin/PrintplatesList";
import OrdersList from "./admin/OrdersList";
import ProductsList from "@material-ui/icons/Storefront";

import FontsList from "./tools/FontsList";
import CollageList from "app/collage/CollageList";

import { ThemeProvider } from "@material-ui/core/styles";
import { darkTheme } from "config/theme";

import UploadProgress from "./tools/UploadProgress";

import { SIZES } from "../constants";

import { useStoreon } from "storeon/react";
import Navigation from "./Navigation";
import { useCanvasSettings } from "utils/canvas";

import t from "../translations";
import { useProboSize } from "utils/probo";

const MenuWrapper = styled.div`
  width: auto;
  position: absolute;
  height: calc(100% - ${SIZES.topBar}px);
  z-index: 101;
  bottom: 0px;
  display: flex;
  @media screen and (max-width: 480px) {
    position: fixed;
    height: auto;
    max-height: ${SIZES.leftBarOpened}px;
    width: 100%;
    bottom: 25px;
    flex-direction: column;
  }
`;
const ButtonsWrapper = styled.div`
  width: ${SIZES.leftBar}px;
  display: flex;
  flex-direction: column;
  background: #262626;
  @media screen and (max-width: 480px) {
    flex-direction: row;
    width: 100%;
  }
`;

const ContentWrapper = styled.div`
  background: #3b3b3b;
  width: ${SIZES.leftBarOpened - SIZES.leftBar}px;
  height: 100%;
  position: relative;
  display: ${(props) => (props.visible ? "block" : "none")};
  @media screen and (max-width: 480px) {
    height: ${SIZES.leftBarOpened - SIZES.leftBar}px;
    width: 100%;
  }
`;

const Wrap = styled.div`
  display: ${(props) => (props.visible ? "block" : "none")};
  width: 100%;
  right: 0px;
  padding: 10px;
  height: 100%;
  position: relative;
  overflow: hidden;
`;

const NavigationWrap = styled.div`
  display: ${(props) => (props.visible ? "block" : "none")};
  width: 100%;
  padding: 0px;
  height: 60px;
  position: relative;
  overflow: hidden;
  background: #3b3b3b;
  > div {
    height: 60px;
  }
`;

const LeftBar = (props) => {
  const {
    dispatch,
    isAdmin,
    activeMenu,
    editorType,
    product,
    config,
  } = useStoreon(
    "isAdmin",
    "activeMenu",
    "editorType",
    "canvas",
    "product",
    "config"
  );

  const { isDynamicSize, isDynamicRadius } = useCanvasSettings();
  const { lockedWidth, lockedHeight } = useProboSize();
  const isFixedPrice = product?.price.type === "fixed";

  const isMobile = window.innerWidth < 480;

  let showSize = true;
  if (product?.proboProduct) {
    if (lockedWidth && lockedHeight) {
      showSize = false;
    }
  } else {
    if (isFixedPrice) {
      showSize = false;
    }
    if (!isDynamicSize && !isDynamicRadius) {
      showSize = false;
    }
  }

  const handleMenuChange = (menuNum) => {
    if (activeMenu === menuNum) {
      // setActiveMenu(0);
      dispatch("menu/close");
    } else {
      // setActiveMenu(menuNum);
      dispatch("menu/setActiveMenu", menuNum);
      // activeMenu === 0 && dispatch("menu/open");
    }
  };

  const MenuButton = (props) => {
    const { num } = props;
    return (
      <ToolbarButton
        dark
        vertical
        active={activeMenu === num}
        onClick={() => handleMenuChange(num)}
        {...props}
      />
    );
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <MenuWrapper>
        <ButtonsWrapper>
          <MenuHideButton
            visible={activeMenu}
            onClick={(_) => {
              dispatch("menu/close");
              // setActiveMenu(0);
            }}
          />
          {!isAdmin && (
            <MenuButton
              num={122}
              desc={t.products}
              onClick={() => dispatch("productSelector/open")}
              icon={<ProductsList />}
            />
          )}
          {isAdmin && (
            <React.Fragment>
              <MenuButton num={7} desc={t.editors} icon={<EditorIcon />} />
              <MenuButton
                num={8}
                desc={t.printplates}
                icon={<PrintplateIcon />}
              />
            </React.Fragment>
          )}
          {editorType === "printplate" && (
            <MenuButton num={11} desc={"Orders"} icon={<OrdersIcon />} />
          )}
          {(showSize || isAdmin) && (
            <MenuButton num={1} desc={t.size} icon={<PhotoSizeSelectIcon />} />
          )}
          {editorType !== "printplate" && (
            <MenuButton num={2} desc={t.gallery} icon={<PhotoLibraryIcon />} />
          )}
          <MenuButton num={3} desc={t.upload} icon={<CloudUploadIcon />}>
            <UploadProgress />
          </MenuButton>

          {(editorType === "printfile" || editorType === "collage") && (
            <MenuButton
              num={99}
              active={activeMenu === 99}
              desc={t.text}
              icon={<TextIcon />}
            />
          )}
          {(editorType === "printfile" || editorType === "collage") &&
            config?.features?.backgroundColor && (
              <MenuButton num={5} desc={t.color} icon={<ColorIcon />} />
            )}
          <MenuButton num={6} desc={t.layers} icon={<LayersIcon />} />
          {(config?.collages || isAdmin) && (
            <MenuButton num={20} desc={t.collages} icon={<ViewCompact />} />
          )}
          {isMobile && (
            <MenuButton num={98} desc={t.navigation} icon={<SettingsIcon />} />
          )}
        </ButtonsWrapper>
        <NavigationWrap visible={activeMenu === 98}>
          <Navigation />
        </NavigationWrap>

        <ContentWrapper visible={activeMenu && activeMenu !== 98}>
          <Wrap visible>
            {activeMenu == 1 && (
              <SizeManager isAdmin={isAdmin} editorType={editorType} />
            )}
            <Wrap style={{padding: 0}} visible={activeMenu == 2}>
              <Gallery />
            </Wrap>
            {/* {activeMenu == 2 && <Gallery />} */}
            {activeMenu == 3 && <Uploader />}
            {activeMenu == 5 && <BackgroundColorPicker />}
            {activeMenu == 6 && <LayersToolbar />}
            {activeMenu == 20 && <CollageList />}
            {activeMenu == 7 && isAdmin && <EditorsList />}
            {activeMenu == 8 && isAdmin && <PrintplatesList />}
            {activeMenu == 11 && isAdmin && <OrdersList />}
            {activeMenu == 99 && <FontsList />}
          </Wrap>
        </ContentWrapper>
      </MenuWrapper>
    </ThemeProvider>
  );
};
export default LeftBar;
